import React, {useMemo, useState} from 'react';
import styles from './BlocksTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, DropdownMenu, Icon} from '@gravity-ui/uikit';
import {Pencil, TrashBin} from '@gravity-ui/icons';
import {DataTable} from '../../shared';
import {BlockModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {EditBlockModal, DeleteBlockModal} from '../../features';
import {useNavigate} from 'react-router';

interface BlocksTableProps {
  blocks: BlockModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
}

export const BlocksTable = ({blocks, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage}: BlocksTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingBlockId, setEditingBlockId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingBlockId, setDeletingBlockId] = useState<string | null>(null);

  const handleRowClick = (block: BlockModel) => {
    navigate(`/content/blocks/${block.id}`);
  };

  const handleEditBlock = (block: BlockModel) => {
    setEditingBlockId(block.id);
    setIsEditModalOpen(true);
  };

  const handleDeleteBlock = (block: BlockModel) => {
    setDeletingBlockId(block.id);
    setIsDeleteModalOpen(true);
  };

  const columns = useMemo<TableColumnConfig<BlockModel>[]>(
    () => [
      {
        id: 'title',
        name: () => (
          <div className={styles.tableHeader}>
            <Text variant="body-1">Заголовок блока</Text>
          </div>
        ),
        template: (item: BlockModel) => <Text variant="body-1">{item.title}</Text>,
      },
      {
        id: 'name',
        name: () => (
          <div className={styles.tableHeader}>
            <Text variant="body-1">Название блока</Text>
          </div>
        ),
        template: (item: BlockModel) => <Text variant="body-1">{item.name}</Text>,
      },
      {
        id: 'more',
        name: () => <div className={styles.tableHeader}></div>,
        template: (item: BlockModel) => (
          <div className={styles.moreCell} onClick={(e) => e.stopPropagation()}>
            <DropdownMenu
              items={[
                {
                  iconStart: <Icon size={16} data={Pencil} />,
                  action: () => handleEditBlock(item),
                  text: 'Редактировать',
                  className: styles.dropdownItem,
                },
                {
                  iconStart: <Icon size={16} data={TrashBin} />,
                  action: () => handleDeleteBlock(item),
                  text: 'Удалить',
                  className: styles.dropdownItemDelete,
                },
              ]}
            />
          </div>
        ),
      },
    ],
    [],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<BlockModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={blocks || []}
        columns={columns}
        edgePadding={false}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Блок не найден"
        emptyStateDescription="Блок с таким названием не найден. Попробуйте изменить запрос"
        onRowClick={handleRowClick}
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}

      <EditBlockModal open={isEditModalOpen} setOpen={setIsEditModalOpen} blockId={editingBlockId} />

      {deletingBlockId && (
        <DeleteBlockModal open={isDeleteModalOpen} setOpen={setIsDeleteModalOpen} blockId={deletingBlockId} />
      )}
    </div>
  );
};
